// @flow

import { combineReducers } from 'redux'
import { DataGridDuck, formActions, createFormReducer, createDataGridReducer } from 'react-redux-components'
import authenticatedRequest from './authenticated-request'
import { addSuccessNotification, addErrorNotification } from './notification-manager'

import type { DataGridState, FormState } from 'react-redux-components'
import type { GetState, Dispatch, ReduxAction } from './index'
import type { NetError } from './authenticated-request'

// Action types
export const UPDATE_VISIT_PENDING: 'visits/UPDATE_VISIT_PENDING' = 'visits/UPDATE_VISIT_PENDING'
export const UPDATE_VISIT_SUCCESS: 'visits/UPDATE_VISIT_SUCCESS' = 'visits/UPDATE_VISIT_SUCCESS'
export const UPDATE_VISIT_ERROR: 'visits/UPDATE_VISIT_ERROR' = 'visits/UPDATE_VISIT_ERROR'

export const DELETE_VISIT_REQUEST: 'visits/DELETE_VISIT_REQUEST' = 'visits/DELETE_VISIT_REQUEST'
export const DELETE_VISIT_CANCEL: 'visits/DELETE_VISIT_CANCEL' = 'visits/DELETE_VISIT_CANCEL'

export const DELETE_VISIT_PENDING: 'visits/DELETE_VISIT_PENDING' = 'visits/DELETE_VISIT_PENDING'
export const DELETE_VISIT_SUCCESS: 'visits/DELETE_VISIT_SUCCESS' = 'visits/DELETE_VISIT_SUCCESS'
export const DELETE_VISIT_ERROR: 'visits/DELETE_VISIT_ERROR' = 'visits/DELETE_VISIT_ERROR'

export type Visit = {
  id_medico: string,
  data_visita: string,
}

type ACTION_UPDATE_VISIT_PENDING = {| type: typeof UPDATE_VISIT_PENDING |}
type ACTION_UPDATE_VISIT_SUCCESS = {| type: typeof UPDATE_VISIT_SUCCESS |}
type ACTION_UPDATE_VISIT_ERROR = {| type: typeof UPDATE_VISIT_ERROR, payload: NetError |}

type ACTION_DELETE_VISIT_REQUEST = {| type: typeof DELETE_VISIT_REQUEST, payload: Visit |}
type ACTION_DELETE_VISIT_CANCEL = {| type: typeof DELETE_VISIT_CANCEL |}

type ACTION_DELETE_VISIT_SUCCESS = {| type: typeof DELETE_VISIT_SUCCESS |}
type ACTION_DELETE_VISIT_PENDING = {| type: typeof DELETE_VISIT_PENDING |}
type ACTION_DELETE_VISIT_ERROR = {| type: typeof DELETE_VISIT_ERROR, payload: NetError |}

export type ManageVisitsAction =
  | ACTION_UPDATE_VISIT_PENDING
  | ACTION_UPDATE_VISIT_SUCCESS
  | ACTION_UPDATE_VISIT_ERROR
  | ACTION_DELETE_VISIT_REQUEST
  | ACTION_DELETE_VISIT_CANCEL
  | ACTION_DELETE_VISIT_PENDING
  | ACTION_DELETE_VISIT_SUCCESS
  | ACTION_DELETE_VISIT_ERROR

const visitForm = {
  id_medico: '',
  nome: '',
  cognome: '',
  data_visita: '',
  uploaded_by: '',
  prodotto: '',
  canale: '',
  isf: '',
  call_experience: '',
  wave: '',
}

type Status = 'NEW' | 'PENDING' | 'SUCCESS' | 'ERROR'
export const statuses = {
  NEW: 'NEW',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
}

type VisitsState_ = {|
  status: Status,
  deleteVisitPending: Visit | false,
|}
type VisitsState = $Shape<VisitsState_>

// Initial state
export const initialState: VisitsState = {
  status: statuses.NEW,
  deleteVisitPending: false,
}

// Reducer
export const reducer = (state: VisitsState = initialState, action: ReduxAction): VisitsState => {
  switch (action.type) {
    case UPDATE_VISIT_PENDING:
    case DELETE_VISIT_PENDING:
      return { ...state, status: statuses.PENDING }
    case UPDATE_VISIT_SUCCESS:
    case DELETE_VISIT_SUCCESS:
      return { ...state, status: statuses.SUCCESS }
    case UPDATE_VISIT_ERROR:
    case DELETE_VISIT_ERROR:
      return { ...state, status: statuses.ERROR }

    case DELETE_VISIT_REQUEST:
      return { ...state, deleteVisitPending: action.payload }
    case DELETE_VISIT_CANCEL:
      return { ...state, deleteVisitPending: false }

    default:
      return state
  }
}

export type ManageVisitsState = {
  ui: VisitsState,
  grid: DataGridState,
  form: FormState,
}
export default combineReducers({
  ui: reducer,
  grid: createDataGridReducer('visitsGrid'),
  form: createFormReducer('visits.form', visitForm),
})

export const onModifyRecord = (record: Visit) => {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(formActions.load('visits.form', record))
    dispatch(DataGridDuck.creators.openSidebar('visitsGrid'))
  }
}

export const cancelEdit = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(formActions.reset('visits.form'))
    dispatch(DataGridDuck.creators.closeSidebar('visitsGrid'))
  }
}

export const updateVisit = (campaignName: string, visit: Visit) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: UPDATE_VISIT_PENDING })
  try {
    let response = await authenticatedRequest.put(
      '/api/visits/' + encodeURIComponent(campaignName) + '/' + visit.id_medico + '/' + visit.data_visita + '/update',
      visit
    )
    dispatch({ type: UPDATE_VISIT_SUCCESS })
    dispatch(DataGridDuck.creators.closeSidebar('visitsGrid'))
    dispatch(addSuccessNotification(response.data.message))
    dispatch(DataGridDuck.creators.getRecords('visitsGrid', {}, authenticatedRequest))
    dispatch(formActions.reset('visits.form'))
  } catch (err) {
    dispatch({ type: UPDATE_VISIT_ERROR, payload: err })
    dispatch(formActions.setPending('visits.form', false))
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const deleteVisitRequest = (visit: Visit): ACTION_DELETE_VISIT_REQUEST => ({
  type: DELETE_VISIT_REQUEST,
  payload: visit,
})
export const deleteVisitCancel = (): ACTION_DELETE_VISIT_CANCEL => ({ type: DELETE_VISIT_CANCEL })
export const deleteVisit = (campaignName: string, visit: Visit) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: DELETE_VISIT_PENDING })
  try {
    let response = await authenticatedRequest.delete(
      '/api/visits/' + encodeURIComponent(campaignName) + '/' + visit.id_medico + '/' + visit.data_visita + '/delete'
    )
    dispatch({ type: DELETE_VISIT_SUCCESS })
    dispatch(addSuccessNotification(response.data.message))
    dispatch(DataGridDuck.creators.getRecords('visitsGrid', {}, authenticatedRequest))
  } catch (err) {
    dispatch({ type: DELETE_VISIT_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
  dispatch(deleteVisitCancel())
}



// WEBPACK FOOTER //
// ./src/store/manage-visits.js